// Rule-based sleep tips generator
// Analyzes mattress data, audio data, and daily journal to generate
// personalised suggestions.

export function generateTips({ sleep, mode, journal, mic }) {
  const tips = [];

  // Convert durations like "2h 15m" to minutes
  const toMin = (s) => {
    if (!s) return 0;
    const h = parseInt((s.match(/(\d+)h/) || [0, 0])[1], 10) || 0;
    const m = parseInt((s.match(/(\d+)m/) || [0, 0])[1], 10) || 0;
    return h * 60 + m;
  };

  // 1) Deep sleep too low
  const deep = toMin(sleep?.deep || "0h 45m");
  if (deep < 60) {
    tips.push({
      icon: "moon",
      category: "Sonno profondo",
      priority: "high",
      title: "Aumenta il sonno profondo",
      description:
        "Hai avuto solo " +
        Math.floor(deep / 60) +
        "h " +
        (deep % 60) +
        "m di sonno profondo. Evita caffeina dopo le 14:00 e fai attività fisica al mattino o pomeriggio.",
    });
  }

  // 2) Takes long to fall asleep
  const fellAsleepIn = parseInt((sleep?.fellAsleepIn || "0").match(/\d+/)?.[0] || 0, 10);
  if (fellAsleepIn > 20) {
    tips.push({
      icon: "timer",
      category: "Addormentamento",
      priority: "medium",
      title: "Ci metti troppo ad addormentarti",
      description:
        fellAsleepIn +
        " min sono tanti. Evita schermi almeno 1 ora prima di dormire e prova una breve meditazione o respirazione 4-7-8.",
    });
  }

  // 3) Heart rate high
  if (sleep?.heartRateAvg && sleep.heartRateAvg > 68) {
    tips.push({
      icon: "heart",
      category: "Battito elevato",
      priority: "medium",
      title: "Battito medio un po' alto",
      description:
        sleep.heartRateAvg +
        " bpm di media. Riduci l'alcol la sera e prova una tisana rilassante alla camomilla o valeriana.",
    });
  }

  // 4) Ambient noise (integrated mode)
  if (mode === "integrated" && mic === "granted") {
    tips.push({
      icon: "volume",
      category: "Rumore",
      priority: "low",
      title: "Ambiente abbastanza silenzioso",
      description:
        "Il livello di rumore è basso. Per migliorare ancora, valuta tende pesanti o un ventilatore soft come rumore bianco.",
    });
  }

  // 5) Cough / breath interruptions (integrated only)
  if (mode === "integrated" && mic === "granted") {
    if (sleep?.coughPerHour > 0.5) {
      tips.push({
        icon: "activity",
        category: "Tosse notturna",
        priority: "high",
        title: "Frequenza della tosse sopra la media",
        description:
          "Più di " +
          sleep.coughPerHour +
          " episodi/h. Usa un umidificatore, tieni la testa un po' rialzata e consulta un medico se persiste.",
      });
    }
    if (sleep?.breathInterruptionsPerHour > 1) {
      tips.push({
        icon: "wind",
        category: "Respiro",
        priority: "high",
        title: "Interruzioni del respiro",
        description:
          "Alcune pause respiratorie rilevate. Consulta uno specialista del sonno per escludere apnee notturne.",
      });
    }
  }

  // 6) Bedtime regularity (if journal has data from previous days we could compare)
  if (sleep?.regularity && sleep.regularity < 75) {
    tips.push({
      icon: "alarm",
      category: "Regolarità",
      priority: "medium",
      title: "Orari di sonno irregolari",
      description:
        "La regolarità è " +
        sleep.regularity +
        "%. Prova ad andare a letto e svegliarti alla stessa ora, anche nel weekend.",
    });
  }

  // ------ Journal-based tips ------
  if (journal) {
    // Caffeine late
    if (journal.caffeine >= 3) {
      tips.push({
        icon: "coffee",
        category: "Caffeina",
        priority: "medium",
        title: "Troppa caffeina oggi",
        description:
          journal.caffeine +
          " tazze di caffè/tè. Prova a limitare a 2 al giorno ed evitare dopo le 14:00 per migliorare il sonno profondo.",
      });
    }
    // Alcohol
    if (journal.alcohol >= 2) {
      tips.push({
        icon: "wine",
        category: "Alcol",
        priority: "medium",
        title: "L'alcol frammenta il sonno",
        description:
          "Hai consumato " +
          journal.alcohol +
          " bevande alcoliche. Anche se concilia l'addormentamento, riduce la fase REM. Prova a ridurre nelle prossime sere.",
      });
    }
    // Stress high
    if (journal.stress >= 7) {
      tips.push({
        icon: "brain",
        category: "Stress",
        priority: "high",
        title: "Stress elevato oggi",
        description:
          "Hai valutato il tuo stress a " +
          journal.stress +
          "/10. Fai 10 minuti di respirazione guidata o scrivi tre cose positive prima di dormire.",
      });
    }
    // No exercise
    if (journal.exercise === "none") {
      tips.push({
        icon: "dumbbell",
        category: "Movimento",
        priority: "low",
        title: "Muoviti di più",
        description:
          "Oggi non hai fatto attività. Anche 20 minuti di camminata al giorno migliorano significativamente la qualità del sonno.",
      });
    }
    // Heavy dinner
    const dinner = (journal.dinner || "").toLowerCase();
    if (/fritt|pizz|carne rossa|pesante|piccante/.test(dinner)) {
      tips.push({
        icon: "utensils",
        category: "Alimentazione",
        priority: "medium",
        title: "Cena pesante",
        description:
          "Una cena pesante può peggiorare la digestione notturna. Prova cene leggere: pesce, verdure, legumi.",
      });
    }
  } else {
    tips.push({
      icon: "notebook",
      category: "Diario",
      priority: "low",
      title: "Compila il tuo diario giornaliero",
      description:
        "Tracciando cibo, caffeina e stress ti daremo consigli ancora più personalizzati.",
    });
  }

  // Always-on good-quality reinforcement
  const score = mode === "mattress" ? sleep?.scoreMattress : sleep?.scoreIntegrated;
  if (score && score >= 85) {
    tips.unshift({
      icon: "sparkles",
      category: "Ottimo lavoro",
      priority: "info",
      title: "Qualità del sonno eccellente",
      description:
        "Continua così! La tua routine sta funzionando. Mantieni gli orari regolari e lo stile di vita attuale.",
    });
  }

  // Sort high > medium > low > info
  const order = { high: 0, medium: 1, low: 2, info: 3 };
  tips.sort((a, b) => order[a.priority] - order[b.priority]);

  return tips;
}
